/**
 * en-GB formatting helpers. Dates are stored in UTC and shown in
 * America/Santiago, in the design system's short form ("28 Sep 2026").
 */
export const APP_TIME_ZONE = "America/Santiago";

export const LOCALE = "en-GB";

export const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const partsFormatter = new Map<string, Intl.DateTimeFormat>();

function zonedParts(date: Date, timeZone: string): Record<"year" | "month" | "day" | "hour" | "minute" | "second", number> {
  let formatter = partsFormatter.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hourCycle: "h23",
    });
    partsFormatter.set(timeZone, formatter);
  }
  const out = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const part of formatter.formatToParts(date)) {
    if (part.type in out) out[part.type as keyof typeof out] = Number(part.value);
  }
  return out;
}

function toDate(value: string | number | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

const pad = (n: number, width = 2): string => String(n).padStart(width, "0");

const relativeFormatter = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto", style: "long" });

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const fmt = {
  number: (n: number): string => new Intl.NumberFormat(LOCALE).format(n),

  money: (n: number): string =>
    new Intl.NumberFormat(LOCALE, { style: "currency", currency: "CLP", currencyDisplay: "narrowSymbol", maximumFractionDigits: 0 }).format(n),

  percent: (n: number, digits = 1): string => `${n.toFixed(digits)}%`,

  /** "28 Sep 2026" */
  date: (value: string | number | Date, timeZone = APP_TIME_ZONE): string => {
    const p = zonedParts(toDate(value), timeZone);
    return `${p.day} ${SHORT_MONTHS[p.month - 1]} ${p.year}`;
  },

  /** "28 Sep 2026, 14:05" */
  dateTime: (value: string | number | Date, timeZone = APP_TIME_ZONE): string => {
    const p = zonedParts(toDate(value), timeZone);
    return `${p.day} ${SHORT_MONTHS[p.month - 1]} ${p.year}, ${pad(p.hour)}:${pad(p.minute)}`;
  },

  /** "14:05:09" (optionally with milliseconds: "14:05:09.123") */
  time: (value: string | number | Date, options: { millis?: boolean } = {}, timeZone = APP_TIME_ZONE): string => {
    const date = toDate(value);
    const p = zonedParts(date, timeZone);
    const base = `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
    return options.millis ? `${base}.${pad(date.getMilliseconds(), 3)}` : base;
  },

  /** "Monday, 28 September 2026 at 14:05:09 (Santiago time)" — for tooltips. */
  dateTimeFull: (value: string | number | Date, timeZone = APP_TIME_ZONE): string =>
    `${new Intl.DateTimeFormat(LOCALE, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
      timeZone,
    }).format(toDate(value))} (Santiago time)`,

  /** "5 minutes ago", "yesterday", "3 days ago"; beyond a week falls back to the short date. */
  relative: (value: string | number | Date, now: number = Date.now()): string => {
    const date = toDate(value);
    const diff = date.getTime() - now;
    const abs = Math.abs(diff);
    if (abs < 45_000) return "just now";
    if (abs < HOUR) return relativeFormatter.format(Math.round(diff / MINUTE), "minute");
    if (abs < DAY) return relativeFormatter.format(Math.round(diff / HOUR), "hour");
    if (abs < 7 * DAY) {
      const today = zonedParts(new Date(now), APP_TIME_ZONE);
      const then = zonedParts(date, APP_TIME_ZONE);
      const dayDiff = Math.round(
        (Date.UTC(then.year, then.month - 1, then.day) - Date.UTC(today.year, today.month - 1, today.day)) / DAY,
      );
      return relativeFormatter.format(dayDiff, "day");
    }
    return fmt.date(date);
  },

  /** Countdown "1:05" (m:ss). */
  countdown: (ms: number): string => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(total / 60)}:${pad(total % 60)}`;
  },

  /** Hour of day in Santiago (0–23), for greetings. */
  hourOfDay: (now: number = Date.now()): number => zonedParts(new Date(now), APP_TIME_ZONE).hour,

  /** "Tuesday 30 Sep" */
  weekdayDate: (now: number = Date.now()): string => {
    const weekday = new Intl.DateTimeFormat(LOCALE, { weekday: "long", timeZone: APP_TIME_ZONE }).format(new Date(now));
    const p = zonedParts(new Date(now), APP_TIME_ZONE);
    return `${weekday} ${p.day} ${SHORT_MONTHS[p.month - 1]}`;
  },

  initials: (name: string): string =>
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase(),
};
